/**
 * LegalTemplates PDF Editor — upload, save, download, print, send, export
 *
 * Split from the former public/js/pdf-editor.js IIFE (Phase 1, Sep 2026).
 * Page config is injected by the Blade view as window.pdfEditorConfig; pdf.js
 * and pdf-lib are bundled from npm through context.js (no CDN).
 */
import { ensureFontkit, ensurePdfLib, libs } from './context.js';
import { fetchUnicodeFontBytes, fontCanRenderChar, isUnicodeFont, isWinAnsiText } from './fonts.js';
import {
    handlePublicSignatureAccess,
    hasPendingAnnotationChanges,
    updateUndoRedoButtons,
} from './annotations.js';
import {
    canvasAreaEl,
    canvasHeaderEl,
    csrfToken,
    downloadButton,
    downloadUrl,
    hintEl,
    loadingEl,
    pagesEl,
    printButton,
    printUrl,
    publicEditorDownloadUrl,
    publicEditorMode,
    saveButton,
    saveCopyButton,
    saveUrl,
    sendCloseButton,
    sendSubmitButton,
    session,
    signatureIframeEl,
    signatureRequestUrl,
    signatureWorkspaceEl,
    state,
    uploadEmptyStateEl,
    workspaceEl,
} from './context.js';
import {
    clamp,
    finiteNumber,
    getBaseFileName,
    hasLoadedDocumentForActions,
    hasPendingEdits,
    hexToRgb,
    normalizeBackgroundColor,
    normalizeInkColor,
    setButtonState,
    toPdfRect,
} from './core.js';
import { pinDocumentUrl, publicDocUrl } from './core.js';
import { clearDraft, hideDraftBanner } from './drafts.js';
import { nudgeBlanksBeforeExport } from './assist.js';
import { startOcrPolling, stopOcrPolling } from './ocr.js';
import {
    hasPendingFormFieldChanges,
    normalizeFormFieldName,
    normalizeFormFieldOverride,
} from './form-fill.js';
import {
    finalizeWatermarkStateAfterSave,
    getProtectDefaultFeedbackMessage,
    getWorkingPdfBytesForConvert,
    hasPendingProtectionChanges,
    hasPendingWatermarkChanges,
    setProtectFeedback,
    setWatermarkFeedback,
    syncProtectEnabledUi,
    syncProtectInputVisibility,
    syncWatermarkInputUi,
    syncWatermarkTabUi,
    updateProtectRulesUi,
} from './modals.js';
import { confirmRedactions, flattenRedactedPages, getRedactedPages, hasPendingRedactions } from './redaction.js';
import {
    ensureDashboardLink,
    ensureHistoryLink,
    syncSaveButtonsForLock,
} from './shell.js';
import {
    clampReplaceMaskToNeighbours,
    commitAllInlineEditors,
    fontFamilyToCss,
    getEditBlockBounds,
    getReplaceInkBox,
    layoutEditBlockLines,
    measureReplaceText,
    resolveStandardFontName,
} from './text.js';
import { setStatus, showAlert, syncDocumentNameUi, syncDocumentRequiredActionsDisabledState } from './ui.js';
import { renderAllPages, renderEmptyThumbPlaceholder } from './viewport.js';

export function showUploadEmptyState() {
    if (!uploadEmptyStateEl) {
        return;
    }

    state.pdfJsDoc = null;
    uploadEmptyStateEl.classList.remove('hidden');
    uploadEmptyStateEl.setAttribute('aria-hidden', 'false');
    loadingEl.classList.add('hidden');
    loadingEl.textContent = 'Loading document pages...';
    pagesEl.innerHTML = '';
    renderEmptyThumbPlaceholder();
    syncDocumentRequiredActionsDisabledState();
}

export function hideUploadEmptyState() {
    if (!uploadEmptyStateEl) {
        return;
    }

    uploadEmptyStateEl.classList.add('hidden');
    uploadEmptyStateEl.setAttribute('aria-hidden', 'true');
}

export function withCacheBust(url) {
    if (!url) {
        return null;
    }

    return url + (url.includes('?') ? '&' : '?') + 'v=' + Date.now();
}

export function resetDocumentEditingStateForUpload() {
    commitAllInlineEditors();

    state.pdfJsDoc = null;
    state.annotations = [];
    state.history = [];
    state.redo = [];
    state.annotationDirty = false;
    state.selectedAnnotationId = null;
    state.draft = null;
    state.pendingImage = null;
    state.formFieldOverrides.clear();
    state.lastActivePage = 1;
    // A new document gets its own draft key once it renders; the previous
    // document's draft stays in IndexedDB in case the same file returns.
    window.clearTimeout(state.localDraft.timer);
    state.localDraft.key = null;
    state.localDraft.restorable = null;
    state.localDraft.pendingRestore = null;
    hideDraftBanner();

    state.protect.enabled = false;
    state.protect.hasPassword = false;
    state.protect.pendingPassword = '';
    state.protect.savedEnabled = false;
    state.protect.savedHasPassword = false;
    state.protect.showPassword = false;
    state.protect.showConfirm = false;
    syncProtectEnabledUi();
    syncProtectInputVisibility();
    updateProtectRulesUi();
    setProtectFeedback(getProtectDefaultFeedbackMessage());

    state.watermark.enabled = false;
    state.watermark.pendingRemoval = false;
    state.watermark.appliedBaseBytes = null;
    state.watermark.type = 'text';
    state.watermark.text = 'CONFIDENTIAL';
    state.watermark.fontFamily = 'Helvetica';
    state.watermark.fontSize = 56;
    state.watermark.color = '#64748b';
    state.watermark.opacity = 0.2;
    state.watermark.rotation = -32;
    state.watermark.imageDataUrl = '';
    state.watermark.imageMimeType = '';
    state.watermark.imageAspectRatio = 1;
    state.watermark.imageScale = 0.45;
    syncWatermarkInputUi();
    syncWatermarkTabUi();
    setWatermarkFeedback('Saved watermarks cannot be undone. Click Apply, then Save Edits to apply permanently.');
    syncDocumentRequiredActionsDisabledState();
}

export function getCsrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return (meta && meta.getAttribute('content')) || csrfToken || '';
}

export function sanitizeDownloadFileName(value) {
    const normalized = String(value || '')
        .replace(/[\\/:*?"<>|]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    return normalized || getBaseFileName();
}

export function setSendBusy(busy) {
    state.send.busy = busy;
    if (sendSubmitButton) {
        sendSubmitButton.disabled = busy;
    }
    if (sendCloseButton) {
        sendCloseButton.disabled = busy;
    }
}

export function hasLoadedDocumentForSend() {
    return hasLoadedDocumentForActions();
}

export function downloadDataUrl(dataUrl, filename) {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
}

export function parseDownloadFileName(contentDisposition, fallbackName) {
    if (!contentDisposition) {
        return fallbackName;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
        try {
            return decodeURIComponent(utf8Match[1]).replace(/[\\/]/g, '_');
        } catch (error) {
            // Keep fallback below.
        }
    }

    const simpleMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    if (simpleMatch && simpleMatch[1]) {
        return simpleMatch[1].replace(/[\\/]/g, '_');
    }

    return fallbackName;
}

export function getApiErrorMessage(payload, fallback = 'Request failed.') {
    if (payload && typeof payload.message === 'string' && payload.message.trim() !== '') {
        return payload.message;
    }

    if (payload && payload.errors && typeof payload.errors === 'object') {
        const firstKey = Object.keys(payload.errors)[0];
        if (firstKey && Array.isArray(payload.errors[firstKey]) && payload.errors[firstKey][0]) {
            return String(payload.errors[firstKey][0]);
        }
    }

    return fallback;
}

export function downloadBlobObject(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadBlobUrl(url, filename) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
}

export function maybePromptToolRatingAfterDownload() {
    if (!publicEditorMode) {
        return;
    }

    if (!window.pdfToolRatingPrompt || typeof window.pdfToolRatingPrompt.show !== 'function') {
        return;
    }

    window.setTimeout(() => {
        window.pdfToolRatingPrompt.show();
    }, 260);
}

export function ensureSignatureEmbedLoaded(forceReload = false) {
    if (!signatureWorkspaceEl || !signatureIframeEl) {
        return;
    }

    if (!signatureRequestUrl) {
        signatureWorkspaceEl.classList.add('active');
        signatureWorkspaceEl.innerHTML = `
            <div class="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                Signature setup route is unavailable for this document.
            </div>
        `;
        return;
    }

    if (session.signatureEmbedInitialized && !forceReload) {
        return;
    }

    const separator = signatureRequestUrl.includes('?') ? '&' : '?';
    signatureIframeEl.src = signatureRequestUrl + separator + 'from=pdf-online-editor&embedded=1&pv=' + Date.now();
    session.signatureEmbedInitialized = true;
}

export function setSignatureWorkspaceMode(enabled) {
    if (!workspaceEl || !canvasHeaderEl || !canvasAreaEl || !signatureWorkspaceEl) {
        return;
    }

    workspaceEl.classList.toggle('signature-mode', enabled);
    canvasHeaderEl.classList.toggle('pdf-force-hidden', enabled);
    hintEl.classList.toggle('pdf-force-hidden', enabled);
    loadingEl.classList.toggle('pdf-force-hidden', enabled || !!state.pdfJsDoc);
    pagesEl.classList.toggle('pdf-force-hidden', enabled);
    signatureWorkspaceEl.classList.toggle('active', enabled);

    if (enabled) {
        ensureSignatureEmbedLoaded(true);
    }
}

export function normalizePdfBytes(input) {
    if (!input) {
        return null;
    }

    if (input instanceof Uint8Array) {
        return input;
    }

    if (input instanceof ArrayBuffer) {
        return new Uint8Array(input);
    }

    if (ArrayBuffer.isView(input) && input.buffer instanceof ArrayBuffer) {
        return new Uint8Array(input.buffer);
    }

    return null;
}

export function hasPdfHeader(bytes) {
    if (!bytes || !bytes.length) {
        return false;
    }

    const maxScan = Math.min(bytes.length - 4, 1024);
    for (let index = 0; index <= maxScan; index++) {
        if (
            bytes[index] === 0x25 && // %
            bytes[index + 1] === 0x50 && // P
            bytes[index + 2] === 0x44 && // D
            bytes[index + 3] === 0x46 && // F
            bytes[index + 4] === 0x2d // -
        ) {
            return true;
        }
    }

    return false;
}

export async function fetchPdfBytes(url) {
    const response = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
            'Accept': 'application/pdf,application/octet-stream;q=0.9,*/*;q=0.5',
        },
    });

    if (!response.ok) {
        throw new Error('Unable to fetch PDF bytes from server.');
    }

    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    const bytes = new Uint8Array(await response.arrayBuffer());

    // If we receive HTML (auth/session/error page), avoid passing invalid bytes to PDF-lib.
    if (contentType.includes('text/html') && !hasPdfHeader(bytes)) {
        throw new Error('Session or response mismatch while loading PDF bytes. Please refresh and try again.');
    }

    return bytes;
}

export async function ensureBasePdfBytesValid() {
    let bytes = normalizePdfBytes(state.basePdfBytes);
    if (bytes && hasPdfHeader(bytes)) {
        return bytes;
    }

    const sourceWithCacheBust = withCacheBust(session.sourceUrl);
    if (!sourceWithCacheBust) {
        throw new Error('No source PDF is available. Upload a PDF to continue.');
    }

    bytes = await fetchPdfBytes(sourceWithCacheBust);
    if (!hasPdfHeader(bytes)) {
        throw new Error('Loaded file is not a valid PDF stream.');
    }

    state.basePdfBytes = bytes;
    return bytes;
}

export async function buildEditedPdfBytes(options = {}) {
    await ensurePdfLib();
    const {
        skipWatermark = false,
        flattenRedactedPages: shouldFlattenRedactedPages = false,
    } = options;
    let baseBytes = await ensureBasePdfBytesValid();
    let pdfDoc;

    try {
        pdfDoc = await libs.PDFLib.PDFDocument.load(baseBytes);
    } catch (error) {
        // Recovery path in case in-memory bytes are stale/corrupt.
        const sourceWithCacheBust = withCacheBust(session.sourceUrl);
        if (!sourceWithCacheBust) {
            throw error;
        }

        baseBytes = await fetchPdfBytes(sourceWithCacheBust);
        state.basePdfBytes = baseBytes;
        pdfDoc = await libs.PDFLib.PDFDocument.load(baseBytes);
    }

    const imageCache = new Map();
    const fontCache = new Map();

    // Standard fonts keep files small and only encode WinAnsi; text that
    // needs more gets the matching Liberation face embedded (subsetted).
    let fontkitRegistered = false;
    async function getEmbeddedFont(standardFontName, sampleText = '') {
        const needsUnicode = !isWinAnsiText(sampleText);
        const cacheKey = needsUnicode ? `unicode:${standardFontName}` : standardFontName;
        if (fontCache.has(cacheKey)) {
            return fontCache.get(cacheKey);
        }

        let embeddedFont = null;
        if (needsUnicode) {
            try {
                const fontkit = await ensureFontkit();
                if (!fontkitRegistered) {
                    pdfDoc.registerFontkit(fontkit);
                    fontkitRegistered = true;
                }
                const bytes = await fetchUnicodeFontBytes(standardFontName);
                embeddedFont = await pdfDoc.embedFont(bytes, { subset: true });
            } catch (error) {
                // Fall back to the standard font; unsupported characters
                // become "?" through the text sanitiser below.
                embeddedFont = null;
            }
        }
        if (!embeddedFont) {
            const standardFont = libs.PDFLib.StandardFonts[standardFontName] || libs.PDFLib.StandardFonts.Helvetica;
            embeddedFont = await pdfDoc.embedFont(standardFont);
        }
        fontCache.set(cacheKey, embeddedFont);

        return embeddedFont;
    }

    for (const annotation of state.annotations) {
        const page = pdfDoc.getPage(annotation.page - 1);
        const layout = state.pageLayouts.get(annotation.page);
        if (!page || !layout) {
            continue;
        }

        const pageSize = page.getSize();
        const xScale = pageSize.width / layout.screenWidth;
        const yScale = pageSize.height / layout.screenHeight;

        if (annotation.type === 'text') {
            const style = {
                fontFamily: annotation.fontFamily || 'Helvetica',
                bold: !!annotation.bold,
                italic: !!annotation.italic,
                underline: !!annotation.underline,
                align: annotation.align || 'left',
            };

            const standardFontName = resolveStandardFontName(style);
            const font = await getEmbeddedFont(standardFontName, annotation.text);
            const fontSize = Math.max(8, Number(annotation.fontSize || 16) * ((xScale + yScale) / 2));
            const lineHeight = fontSize * 1.25;
            const lines = String(annotation.text || '').split(/\n/);
            const boxWidth = Math.max(80, Number(annotation.boxWidth || 240) * xScale);
            const startX = annotation.x * xScale;
            const topY = annotation.y * yScale;
            const color = hexToRgb(normalizeInkColor(annotation.color || '#111827'), '#111827');

            lines.forEach((lineRaw, index) => {
                const line = lineRaw.length ? lineRaw : ' ';
                const textWidth = font.widthOfTextAtSize(line, fontSize);
                const y = pageSize.height - topY - fontSize - (index * lineHeight);
                let x = startX;

                if (style.align === 'center') {
                    x = startX + Math.max(0, (boxWidth - textWidth) / 2);
                } else if (style.align === 'right') {
                    x = startX + Math.max(0, boxWidth - textWidth);
                }

                page.drawText(line, {
                    x,
                    y,
                    size: fontSize,
                    font,
                    color,
                });

                if (style.underline) {
                    const underlineY = y - Math.max(1, fontSize * 0.08);
                    page.drawLine({
                        start: { x, y: underlineY },
                        end: { x: x + textWidth, y: underlineY },
                        color,
                        thickness: Math.max(0.8, fontSize * 0.05),
                    });
                }
            });
            continue;
        }

        if (annotation.type === 'edit-block') {
            const backgroundColor = hexToRgb(normalizeBackgroundColor(annotation.backgroundColor || '#ffffff'), '#ffffff');
            const drawScale = (xScale + yScale) / 2;

            // Embed every font used by the block's runs up front so the
            // layout pass below can measure synchronously.
            const runFonts = new Map();
            for (const line of (annotation.lines || [])) {
                for (const run of (line.runs || [])) {
                    const standardFontName = resolveStandardFontName({
                        fontFamily: run.fontFamily || 'Helvetica',
                        bold: !!run.bold,
                        italic: !!run.italic,
                    });
                    // Runs are cached per face; one non-WinAnsi run upgrades
                    // that face to its Unicode twin for the whole block.
                    const runNeedsUnicode = !isWinAnsiText(run.text || '');
                    if (!runFonts.has(standardFontName) || (runNeedsUnicode && !isUnicodeFont(runFonts.get(standardFontName)))) {
                        runFonts.set(standardFontName, await getEmbeddedFont(standardFontName, runNeedsUnicode ? run.text : ''));
                    }
                }
            }

            const safeTextCache = new Map();
            const safeRunText = (run, font) => {
                const raw = String(run.text || '').replace(/\r/g, '');
                const cacheKey = `${raw}::${run.fontFamily}${run.bold ? 'b' : ''}${run.italic ? 'i' : ''}`;
                if (safeTextCache.has(cacheKey)) {
                    return safeTextCache.get(cacheKey);
                }

                let safe;
                if (isUnicodeFont(font)) {
                    // An embedded Liberation face keeps typographic
                    // punctuation; only glyphs it truly lacks become "?".
                    safe = Array.from(raw.replace(/\u00A0/g, ' ').replace(/\t/g, '    '))
                        .map((char) => (fontCanRenderChar(font, char) ? char : '?'))
                        .join('');
                } else {
                    safe = raw
                        .replace(/[‘’‚]/g, "'")
                        .replace(/[“”„]/g, '"')
                        .replace(/[–—]/g, '-')
                        .replace(/…/g, '...')
                        .replace(/\u00A0/g, ' ')
                        .replace(/[•●▪]/g, '*')
                        .replace(/\t/g, '    ');
                    try {
                        font.widthOfTextAtSize(safe, 10);
                    } catch (error) {
                        safe = safe.split('').map((char) => {
                            try {
                                font.widthOfTextAtSize(char, 10);
                                return char;
                            } catch (innerError) {
                                return '?';
                            }
                        }).join('');
                    }
                }

                safeTextCache.set(cacheKey, safe);
                return safe;
            };

            // Lay the runs out with the SAME measurement the on-screen
            // preview uses (canvas metrics of the CSS face), so every run
            // starts where the person saw it. The PDF faces are metric
            // twins (Helvetica/Arial, Times, Courier, Liberation), so the
            // drawn glyphs land inside those boxes.
            const placed = layoutEditBlockLines(annotation, (run) => {
                const cssFont = {
                    family: fontFamilyToCss(run.fontFamily || 'Helvetica'),
                    weight: run.bold ? '700' : '400',
                    style: run.italic ? 'italic' : 'normal',
                };
                return measureReplaceText(run.text, cssFont, Math.max(8, Number(run.fontSize || 12)));
            });

            const bounds = getEditBlockBounds(annotation);
            const maskX = Math.max(0, (bounds.x - 2) * xScale);
            const maskTopScreen = Math.max(0, bounds.y - 1.5);
            const maskBottomScreen = bounds.y + bounds.height + 1.5;
            page.drawRectangle({
                x: maskX,
                y: pageSize.height - (maskBottomScreen * yScale),
                width: Math.max(1, (bounds.width + 4) * xScale),
                height: Math.max(1, (maskBottomScreen - maskTopScreen) * yScale),
                color: backgroundColor,
                opacity: 1,
            });

            for (const entry of placed) {
                for (const placedRun of entry.runs) {
                    const run = placedRun.run;
                    const standardFontName = resolveStandardFontName({
                        fontFamily: run.fontFamily || 'Helvetica',
                        bold: !!run.bold,
                        italic: !!run.italic,
                    });
                    const font = runFonts.get(standardFontName);
                    const size = Math.max(6.5, Number(run.fontSize || 12) * drawScale);
                    const output = safeRunText(run, font);
                    if (!output.trim()) {
                        continue;
                    }

                    page.drawText(output, {
                        x: placedRun.x * xScale,
                        y: pageSize.height - (entry.baselineY * yScale),
                        size,
                        font,
                        color: hexToRgb(normalizeInkColor(run.color || '#111827'), '#111827'),
                    });
                }
            }
            continue;
        }

        if (annotation.type === 'replace-text') {
            const style = {
                fontFamily: annotation.fontFamily || 'Helvetica',
                bold: !!annotation.bold,
                italic: !!annotation.italic,
                underline: false,
                align: annotation.align || 'left',
            };

            const standardFontName = resolveStandardFontName(style);
            const font = await getEmbeddedFont(standardFontName, annotation.text);
            const box = toPdfRect(annotation, layout, pageSize);
            const color = hexToRgb(normalizeInkColor(annotation.color || '#111827'), '#111827');
            const backgroundColor = hexToRgb(normalizeBackgroundColor(annotation.backgroundColor || '#ffffff'), '#ffffff');
            const textValue = String(annotation.text || '').replace(/\r/g, '');

            // Clamp the drawable/mask width to the containing cell (bug #2): the committed width is
            // already constrained, but a stored rightBoundary lets us be precise across re-renders.
            const storedRightBoundary = finiteNumber(annotation.rightBoundary, Infinity);
            const boundaryRightScreen = Number.isFinite(storedRightBoundary)
                ? storedRightBoundary - 0.5
                : Infinity;
            const cellRightScreen = Math.min(
                Number(annotation.x || 0) + Number(annotation.width || 0),
                boundaryRightScreen
            );
            const availableWidth = Math.max(4, (cellRightScreen - Number(annotation.x || 0)) * box.xScale);
            let drawSize = Math.max(7, Number(annotation.fontSize || 12) * ((xScale + yScale) / 2));
            let measure = textValue ? font.widthOfTextAtSize(textValue, drawSize) : 0;

            while (textValue && measure > availableWidth && drawSize > 6.5) {
                drawSize -= 0.35;
                measure = font.widthOfTextAtSize(textValue, drawSize);
            }

            let output = textValue;
            if (textValue && measure > availableWidth) {
                let truncated = textValue;
                while (truncated.length > 1 && font.widthOfTextAtSize(`${truncated}...`, drawSize) > availableWidth) {
                    truncated = truncated.slice(0, -1);
                }
                output = truncated.length ? `${truncated}...` : ' ';
                measure = font.widthOfTextAtSize(output, drawSize);
            }

            // Tighten the mask to the glyph ink box so it can't clip the neighbouring line (bug #1),
            // then clamp it so it never crosses into the line above/below.
            const inkBox = getReplaceInkBox(annotation);
            const maskBounds = clampReplaceMaskToNeighbours(annotation.page, annotation, inkBox);
            const maskPadX = Math.max(0.9, box.xScale * 1.0);
            const maskPadY = Math.max(0.4, box.yScale * 0.4);
            const maskLeft = box.x - maskPadX;
            const maskRight = Math.min(
                (Number(annotation.x || 0) + Number(annotation.width || 0)) * box.xScale + maskPadX,
                cellRightScreen * box.xScale + maskPadX
            );
            const maskTopScreen = maskBounds.top;
            const maskBottomScreen = maskBounds.bottom;
            page.drawRectangle({
                x: maskLeft,
                y: pageSize.height - (maskBottomScreen * box.yScale) - maskPadY,
                width: Math.max(1, maskRight - maskLeft),
                height: ((maskBottomScreen - maskTopScreen) * box.yScale) + (maskPadY * 2),
                color: backgroundColor,
                opacity: 1,
            });

            if (!output) {
                continue;
            }

            let textX = box.x;
            if (style.align === 'center') {
                textX = box.x + Math.max(0.5, (availableWidth - measure) / 2);
            } else if (style.align === 'right') {
                textX = box.x + Math.max(0.5, availableWidth - measure);
            }

            const textY = Number.isFinite(Number(annotation.baselineY))
                ? pageSize.height - (Number(annotation.baselineY) * box.yScale)
                : box.y + Math.max(0.8, (box.height - drawSize) / 2) + (drawSize * 0.82);

            page.drawText(output, {
                x: textX,
                y: textY,
                size: drawSize,
                font,
                color,
            });
            continue;
        }

        if (annotation.type === 'comment') {
            const commentFont = await getEmbeddedFont('Helvetica', annotation.text);
            const pinSize = Math.max(18, Number(annotation.pinSize || 22));
            const pinWidth = pinSize * xScale;
            const pinHeight = pinSize * yScale;
            const pinX = annotation.x * xScale;
            const pinTopY = annotation.y * yScale;
            const pinCenterX = pinX + (pinWidth / 2);
            const pinCenterY = pageSize.height - pinTopY - (pinHeight / 2);
            const pinRadius = Math.max(4, Math.min(pinWidth, pinHeight) / 2);

            page.drawCircle({
                x: pinCenterX,
                y: pinCenterY,
                size: pinRadius,
                color: libs.PDFLib.rgb(0.996, 0.949, 0.78),
                borderColor: libs.PDFLib.rgb(0.957, 0.62, 0.043),
                borderWidth: 1.1,
            });

            page.drawText('C', {
                x: pinCenterX - (pinRadius * 0.42),
                y: pinCenterY - (pinRadius * 0.45),
                size: Math.max(8, pinRadius * 1.08),
                font: commentFont,
                color: libs.PDFLib.rgb(0.471, 0.208, 0.016),
            });

            const noteFontSize = Math.max(8.5, 10.5 * ((xScale + yScale) / 2));
            const lineHeight = noteFontSize * 1.2;
            const maxLines = 2;
            const rawValue = String(annotation.text || '').replace(/\r/g, '').trim();
            const rawLines = (rawValue || 'Comment').split(/\n/);
            const maxNoteWidth = Math.max(90, Math.min(pageSize.width * 0.36, 220 * xScale));
            const maxTextWidth = Math.max(44, maxNoteWidth - 12);
            const wrappedLines = [];

            rawLines.forEach((rawLine) => {
                const words = rawLine.split(/\s+/).filter(Boolean);
                if (!words.length) {
                    wrappedLines.push(' ');
                    return;
                }

                let currentLine = '';
                words.forEach((word) => {
                    const candidate = currentLine ? `${currentLine} ${word}` : word;
                    const candidateWidth = commentFont.widthOfTextAtSize(candidate, noteFontSize);
                    if (candidateWidth <= maxTextWidth || currentLine === '') {
                        currentLine = candidate;
                    } else {
                        wrappedLines.push(currentLine);
                        currentLine = word;
                    }
                });

                if (currentLine) {
                    wrappedLines.push(currentLine);
                }
            });

            let outputLines = wrappedLines.slice(0, maxLines);
            if (wrappedLines.length > maxLines && outputLines.length) {
                let tail = outputLines[outputLines.length - 1];
                while (tail.length > 1 && commentFont.widthOfTextAtSize(`${tail}...`, noteFontSize) > maxTextWidth) {
                    tail = tail.slice(0, -1);
                }
                outputLines[outputLines.length - 1] = `${tail}...`;
            }

            const longestLineWidth = outputLines.reduce((maxWidth, line) => {
                return Math.max(maxWidth, commentFont.widthOfTextAtSize(line || ' ', noteFontSize));
            }, 0);

            const noteWidth = Math.max(74, Math.min(maxNoteWidth, longestLineWidth + 14));
            const noteHeight = Math.max(20, (outputLines.length * lineHeight) + 8);
            let noteX = pinX + pinWidth + 7;
            if (noteX + noteWidth > pageSize.width - 6) {
                noteX = Math.max(6, pinX - noteWidth - 7);
            }

            const noteTopY = clamp(
                pinTopY + (pinHeight / 2) - (noteHeight / 2),
                0,
                Math.max(1, pageSize.height - noteHeight)
            );
            const noteY = pageSize.height - noteTopY - noteHeight;

            page.drawRectangle({
                x: noteX,
                y: noteY,
                width: noteWidth,
                height: noteHeight,
                color: libs.PDFLib.rgb(0.996, 0.949, 0.78),
                borderColor: libs.PDFLib.rgb(0.957, 0.62, 0.043),
                borderWidth: 1,
            });

            const connectorStartX = noteX > pinCenterX ? pinCenterX + pinRadius : pinCenterX - pinRadius;
            const connectorEndX = noteX > pinCenterX ? noteX : (noteX + noteWidth);
            const connectorY = noteY + (noteHeight / 2);
            page.drawLine({
                start: { x: connectorStartX, y: pinCenterY },
                end: { x: connectorEndX, y: connectorY },
                color: libs.PDFLib.rgb(0.957, 0.62, 0.043),
                thickness: 1,
            });

            let textY = noteY + noteHeight - noteFontSize - 4;
            outputLines.forEach((line) => {
                if (textY < (noteY + 4)) {
                    return;
                }

                page.drawText(line, {
                    x: noteX + 6,
                    y: textY,
                    size: noteFontSize,
                    font: commentFont,
                    color: libs.PDFLib.rgb(0.471, 0.208, 0.016),
                });

                textY -= lineHeight;
            });
            continue;
        }

        if (annotation.type === 'shape') {
            const box = toPdfRect(annotation, layout, pageSize);
            page.drawRectangle({
                x: box.x,
                y: box.y,
                width: box.width,
                height: box.height,
                borderColor: hexToRgb(annotation.color, '#1d4ed8'),
                borderWidth: Math.max(1, annotation.stroke * xScale),
                color: libs.PDFLib.rgb(1, 1, 1),
                opacity: 0,
            });
            continue;
        }

        if (annotation.type === 'highlight') {
            const box = toPdfRect(annotation, layout, pageSize);
            page.drawRectangle({
                x: box.x,
                y: box.y,
                width: box.width,
                height: box.height,
                color: hexToRgb(annotation.color, '#fde047'),
                opacity: 0.35,
            });
            continue;
        }

        if (annotation.type === 'erase') {
            const box = toPdfRect(annotation, layout, pageSize);
            page.drawRectangle({
                x: box.x,
                y: box.y,
                width: box.width,
                height: box.height,
                color: libs.PDFLib.rgb(1, 1, 1),
                opacity: 1,
            });
            continue;
        }

        if (annotation.type === 'redact') {
            const box = toPdfRect(annotation, layout, pageSize);
            page.drawRectangle({
                x: box.x,
                y: box.y,
                width: box.width,
                height: box.height,
                color: libs.PDFLib.rgb(0, 0, 0),
                opacity: 1,
            });
            continue;
        }

        if (annotation.type === 'underline') {
            const startX = annotation.x * xScale;
            const endX = (annotation.x + annotation.width) * xScale;
            const lineY = pageSize.height - (annotation.y * yScale);
            page.drawLine({
                start: { x: startX, y: lineY },
                end: { x: endX, y: lineY },
                color: hexToRgb(annotation.color, '#1d4ed8'),
                thickness: Math.max(1, annotation.stroke * xScale),
            });
            continue;
        }

        if (annotation.type === 'draw') {
            const points = annotation.points || [];
            if (points.length < 2) {
                continue;
            }

            for (let index = 1; index < points.length; index++) {
                const start = points[index - 1];
                const end = points[index];
                page.drawLine({
                    start: {
                        x: start.x * xScale,
                        y: pageSize.height - (start.y * yScale),
                    },
                    end: {
                        x: end.x * xScale,
                        y: pageSize.height - (end.y * yScale),
                    },
                    color: hexToRgb(annotation.color, '#1d4ed8'),
                    thickness: Math.max(1, annotation.stroke * xScale),
                });
            }
            continue;
        }

        if (annotation.type === 'image') {
            const box = toPdfRect(annotation, layout, pageSize);

            let embeddedImage = imageCache.get(annotation.dataUrl);
            if (!embeddedImage) {
                const imageDataBuffer = await fetch(annotation.dataUrl).then((response) => response.arrayBuffer());
                if ((annotation.mimeType || '').toLowerCase().includes('png')) {
                    embeddedImage = await pdfDoc.embedPng(imageDataBuffer);
                } else {
                    embeddedImage = await pdfDoc.embedJpg(imageDataBuffer);
                }
                imageCache.set(annotation.dataUrl, embeddedImage);
            }

            page.drawImage(embeddedImage, {
                x: box.x,
                y: box.y,
                width: box.width,
                height: box.height,
            });
        }
    }

    if (state.formFieldOverrides.size > 0) {
        const fieldMetaByName = new Map();
        for (const fields of state.pageFormFields.values()) {
            if (!Array.isArray(fields)) {
                continue;
            }
            fields.forEach((field) => {
                if (!field) {
                    return;
                }
                const key = normalizeFormFieldName(field.name, field.id);
                if (!fieldMetaByName.has(key)) {
                    fieldMetaByName.set(key, field);
                }
            });
        }

        let form = null;
        let formFont = null;
        try {
            form = pdfDoc.getForm();
            formFont = await getEmbeddedFont('Helvetica');
        } catch (error) {
            form = null;
            formFont = null;
        }

        const fallbackFont = await getEmbeddedFont('Helvetica');
        const fallbackTextColor = hexToRgb('#111827', '#111827');
        const fallbackCheckColor = hexToRgb('#1f2937', '#1f2937');
        let updatedTextCount = 0;

        for (const [fieldName, overrideRaw] of state.formFieldOverrides.entries()) {
            const override = normalizeFormFieldOverride(overrideRaw);
            const fieldMeta = fieldMetaByName.get(fieldName) || null;
            let appliedToNativeField = false;

            if (form) {
                if (override.kind === 'checkbox') {
                    let checkField = null;
                    try {
                        checkField = form.getCheckBox(fieldName);
                    } catch (error) {
                        try {
                            checkField = form.getField(fieldName);
                        } catch (innerError) {
                            checkField = null;
                        }
                    }

                    if (checkField && typeof checkField.check === 'function' && typeof checkField.uncheck === 'function') {
                        if (override.checked) {
                            checkField.check();
                        } else {
                            checkField.uncheck();
                        }
                        appliedToNativeField = true;
                    }
                } else {
                    const textValue = String(override.value ?? '');
                    let textField = null;
                    try {
                        textField = form.getTextField(fieldName);
                    } catch (error) {
                        try {
                            textField = form.getField(fieldName);
                        } catch (innerError) {
                            textField = null;
                        }
                    }

                    if (textField && typeof textField.setText === 'function') {
                        textField.setText(textValue);
                        updatedTextCount += 1;
                        appliedToNativeField = true;
                    }
                }
            }

            if (appliedToNativeField || !fieldMeta || !fieldMeta.heuristic || fieldMeta.readOnly) {
                continue;
            }

            const targetPageNumber = Number(fieldMeta.page || 0);
            const page = pdfDoc.getPage(targetPageNumber - 1);
            const layout = state.pageLayouts.get(targetPageNumber);
            if (!page || !layout) {
                continue;
            }

            const pageSize = page.getSize();
            const fieldBox = toPdfRect({
                x: Number(fieldMeta.x || 0),
                y: Number(fieldMeta.y || 0),
                width: Math.max(1, Number(fieldMeta.width || 0)),
                height: Math.max(1, Number(fieldMeta.height || 0)),
            }, layout, pageSize);

            if (override.kind === 'checkbox') {
                if (!override.checked) {
                    continue;
                }

                const padding = Math.max(0.8, Math.min(fieldBox.width, fieldBox.height) * 0.24);
                page.drawLine({
                    start: { x: fieldBox.x + padding, y: fieldBox.y + padding },
                    end: { x: fieldBox.x + fieldBox.width - padding, y: fieldBox.y + fieldBox.height - padding },
                    color: fallbackCheckColor,
                    thickness: Math.max(1, Math.min(fieldBox.width, fieldBox.height) * 0.11),
                });
                page.drawLine({
                    start: { x: fieldBox.x + padding, y: fieldBox.y + fieldBox.height - padding },
                    end: { x: fieldBox.x + fieldBox.width - padding, y: fieldBox.y + padding },
                    color: fallbackCheckColor,
                    thickness: Math.max(1, Math.min(fieldBox.width, fieldBox.height) * 0.11),
                });
                continue;
            }

            const rawTextValue = String(override.value ?? '').replace(/\r/g, '').replace(/\n+/g, ' ');
            if (!rawTextValue.trim()) {
                continue;
            }

            const availableWidth = Math.max(10, fieldBox.width - 4);
            const isLineBasedHeuristic = String(fieldMeta.heuristicSource || '') === 'line';
            const baseScreenFontSize = isLineBasedHeuristic
                ? Math.max(10, Math.min(16, Number(fieldMeta.height || 18) * 0.58))
                : Math.max(11, Math.min(18, Number(fieldMeta.height || 18) * 0.74));
            let drawSize = Math.max(7.5, baseScreenFontSize * ((fieldBox.xScale + fieldBox.yScale) / 2));
            let output = rawTextValue;
            let measuredWidth = fallbackFont.widthOfTextAtSize(output, drawSize);

            while (drawSize > 6.8 && measuredWidth > availableWidth) {
                drawSize -= 0.35;
                measuredWidth = fallbackFont.widthOfTextAtSize(output, drawSize);
            }

            if (measuredWidth > availableWidth) {
                let trimmed = output;
                while (trimmed.length > 1 && fallbackFont.widthOfTextAtSize(`${trimmed}...`, drawSize) > availableWidth) {
                    trimmed = trimmed.slice(0, -1);
                }
                output = trimmed.length ? `${trimmed}...` : ' ';
            }

            let textY = fieldBox.y + Math.max(1, (fieldBox.height - drawSize) / 2);

            if (isLineBasedHeuristic) {
                const lineYScreen = Number.isFinite(Number(fieldMeta.lineYScreen))
                    ? Number(fieldMeta.lineYScreen)
                    : (Number(fieldMeta.y || 0) + Number(fieldMeta.height || 0));
                const lineYPdf = pageSize.height - (lineYScreen * fieldBox.yScale);
                textY = lineYPdf + Math.max(1.2, drawSize * 0.28);
                textY = clamp(
                    textY,
                    fieldBox.y + 0.8,
                    fieldBox.y + Math.max(1.2, fieldBox.height - (drawSize * 0.05))
                );
            }

            page.drawText(output, {
                x: fieldBox.x + 1.8,
                y: textY,
                size: drawSize,
                font: fallbackFont,
                color: fallbackTextColor,
            });
        }

        if (form && formFont && updatedTextCount > 0) {
            try {
                form.updateFieldAppearances(formFont);
            } catch (error) {
                // Some PDFs have malformed resources; values still persist.
            }
        }
    }

    if (state.watermark.enabled && !skipWatermark) {
        const opacity = clamp(Number(state.watermark.opacity || 0.2), 0.05, 0.9);
        const rotation = clamp(Number(state.watermark.rotation || 0), -90, 90);

        if (state.watermark.type === 'image') {
            if (state.watermark.imageDataUrl) {
                const imageDataBuffer = await fetch(state.watermark.imageDataUrl).then((response) => response.arrayBuffer());
                const isPng = String(state.watermark.imageMimeType || '').toLowerCase().includes('png');
                const embeddedImage = isPng
                    ? await pdfDoc.embedPng(imageDataBuffer)
                    : await pdfDoc.embedJpg(imageDataBuffer);

                for (let pageNumber = 1; pageNumber <= pdfDoc.getPageCount(); pageNumber += 1) {
                    const page = pdfDoc.getPage(pageNumber - 1);
                    const layout = state.pageLayouts.get(pageNumber);
                    if (!page || !layout) {
                        continue;
                    }

                    const pageSize = page.getSize();
                    const aspectRatio = Math.max(0.05, Number(state.watermark.imageAspectRatio || 1));
                    const scale = clamp(Number(state.watermark.imageScale || 0.45), 0.15, 0.85);
                    let width = pageSize.width * scale;
                    let height = width / aspectRatio;
                    const maxHeight = pageSize.height * 0.8;
                    if (height > maxHeight) {
                        height = maxHeight;
                        width = height * aspectRatio;
                    }

                    const centerX = pageSize.width / 2;
                    const centerY = pageSize.height / 2;
                    const radians = rotation * (Math.PI / 180);
                    const x = centerX - ((width / 2) * Math.cos(radians) - (height / 2) * Math.sin(radians));
                    const y = centerY - ((width / 2) * Math.sin(radians) + (height / 2) * Math.cos(radians));

                    page.drawImage(embeddedImage, {
                        x,
                        y,
                        width,
                        height,
                        opacity,
                        rotate: libs.PDFLib.degrees(rotation),
                    });
                }
            }
        } else {
            const text = String(state.watermark.text || '').trim();
            if (text) {
                const watermarkFont = await getEmbeddedFont(resolveStandardFontName({
                    fontFamily: state.watermark.fontFamily || 'Helvetica',
                    bold: true,
                    italic: false,
                }));
                const color = hexToRgb(state.watermark.color, '#64748b');

                for (let pageNumber = 1; pageNumber <= pdfDoc.getPageCount(); pageNumber += 1) {
                    const page = pdfDoc.getPage(pageNumber - 1);
                    const layout = state.pageLayouts.get(pageNumber);
                    if (!page || !layout) {
                        continue;
                    }

                    const pageSize = page.getSize();
                    const xScale = pageSize.width / layout.screenWidth;
                    const yScale = pageSize.height / layout.screenHeight;
                    const scaleAverage = (xScale + yScale) / 2;
                    const fontSize = Math.max(10, Number(state.watermark.fontSize || 56) * scaleAverage);
                    const textWidth = watermarkFont.widthOfTextAtSize(text, fontSize);
                    const textHeight = fontSize;
                    const centerX = pageSize.width / 2;
                    const centerY = pageSize.height / 2;
                    const radians = rotation * (Math.PI / 180);
                    const x = centerX - ((textWidth / 2) * Math.cos(radians) - (textHeight / 2) * Math.sin(radians));
                    const y = centerY - ((textWidth / 2) * Math.sin(radians) + (textHeight / 2) * Math.cos(radians));

                    page.drawText(text, {
                        x,
                        y,
                        size: fontSize,
                        font: watermarkFont,
                        color,
                        opacity,
                        rotate: libs.PDFLib.degrees(rotation),
                    });
                }
            }
        }
    }

    if (shouldFlattenRedactedPages) {
        // Boxes are drawn; now make the covered text actually go away.
        await flattenRedactedPages(pdfDoc, getRedactedPages());
    }

    return pdfDoc.save();
}

export function downloadBlob(bytes, filename) {
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Write the given document bytes as the working document (session file or
 * member document) and re-render. Used by undo/redo of page operations; it
 * never bakes annotations — those live in the edit snapshot being restored.
 */
export async function persistDocumentBytes(bytes) {
    if (!saveUrl) {
        throw new Error('Save endpoint is unavailable for this document.');
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const formData = new FormData();
    formData.append('file', blob, `${session.docName || 'document'}.pdf`);
    formData.append('doc_name', String(session.docName || 'Edited PDF'));

    const response = await fetch(publicDocUrl(saveUrl), {
        method: 'POST',
        headers: {
            'X-CSRF-TOKEN': getCsrfToken(),
            'Accept': 'application/json',
        },
        body: formData,
        credentials: 'same-origin',
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(getApiErrorMessage(payload, 'Could not restore the document.'));
    }
    if (payload && payload.source_url) {
        session.sourceUrl = payload.source_url;
    }
    state.basePdfBytes = new Uint8Array(bytes);
    state.selectedAnnotationId = null;
    state.interaction = null;
    state.draft = null;
    await renderAllPages();
}

export async function saveEdits(options = {}) {
    const {
        silentNoEdits = false,
        silentSuccess = false,
        saveAsCopy = false,
    } = options;
    const effectiveSaveAsCopy = saveAsCopy || session.lockedForDirectEdit;

    commitAllInlineEditors();

    const hasAnnotationChanges = hasPendingAnnotationChanges();
    const hasWatermarkChanges = hasPendingWatermarkChanges();
    const hasFormFieldChanges = hasPendingFormFieldChanges();
    const hasVisualChanges = hasAnnotationChanges || hasWatermarkChanges || hasFormFieldChanges;
    const hasProtectionChanges = hasPendingProtectionChanges();

    if (!hasVisualChanges && !hasProtectionChanges) {
        if (!silentNoEdits) {
            showAlert('No unsaved edits found. Add annotations, fill fields, or update protection first.');
        }
        return true;
    }

    // Redactions are irreversible once written: the person reviews them first.
    const applyRedactions = hasAnnotationChanges && hasPendingRedactions();
    if (applyRedactions) {
        const confirmed = await confirmRedactions();
        if (!confirmed) {
            setStatus('Save cancelled');
            return false;
        }
    }

    setButtonState(saveButton, true);
    if (saveCopyButton) {
        setButtonState(saveCopyButton, true);
    }
    setStatus(applyRedactions ? 'Applying redactions and saving...' : 'Saving edits...');

    try {
        if (!hasVisualChanges) {
            await ensureBasePdfBytesValid();
        }

        const editedBytes = hasVisualChanges
            ? await buildEditedPdfBytes({ flattenRedactedPages: applyRedactions })
            : new Uint8Array(state.basePdfBytes || []);

        if (publicEditorMode) {
            let publicSavePayload = null;
            if (saveUrl) {
                const blob = new Blob([editedBytes], { type: 'application/pdf' });
                const formData = new FormData();
                formData.append('file', blob, `${session.docName || 'document'}.pdf`);
                formData.append('doc_name', String(session.docName || 'Edited PDF'));
                if (applyRedactions) {
                    // The server rewrites the file so the replaced pages'
                    // objects are dropped, not merely unreferenced, and
                    // runs OCR on the flattened pages to restore search.
                    formData.append('redacted', '1');
                    formData.append('redacted_pages', getRedactedPages().join(','));
                }
                if (effectiveSaveAsCopy) {
                    formData.append('save_mode', 'copy');
                }
                if (hasProtectionChanges) {
                    formData.append('protect_enabled', state.protect.enabled ? '1' : '0');
                    if (state.protect.enabled && state.protect.pendingPassword) {
                        formData.append('protect_password', state.protect.pendingPassword);
                    }
                }

                const response = await fetch(publicDocUrl(saveUrl), {
                    method: 'POST',
                    headers: {
                        'X-CSRF-TOKEN': getCsrfToken(),
                        'Accept': 'application/json',
                    },
                    body: formData,
                    credentials: 'same-origin',
                });

                const payload = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(getApiErrorMessage(payload, 'Failed to save edits.'));
                }
                publicSavePayload = payload;

                if (payload && payload.source_url) {
                    session.sourceUrl = payload.source_url;
                }
                if (payload && payload.doc_name) {
                    session.docName = String(payload.doc_name);
                }
                if (payload && payload.doc_id) {
                    session.docId = String(payload.doc_id);
                    pinDocumentUrl();
                }
                if (payload && payload.ocr === 'queued' && payload.ocr_status_url) {
                    startOcrPolling(payload.ocr_status_url);
                } else {
                    stopOcrPolling();
                }
                if (payload && payload.saved_to_dashboard) {
                    // The tab may now be on a fresh copy (e-sign lock) — the
                    // lock, the label and the History link follow the server.
                    session.lockedForDirectEdit = Boolean(payload.document_locked_for_direct_edit);
                    syncSaveButtonsForLock();
                    if (payload.history_url) {
                        ensureHistoryLink(payload.history_url);
                    }
                }
            }

            // Members stay right here after saving. The server keeps writing
            // the same dashboard document on every save (session-bound
            // UserPdf), so a quiet link is all the confirmation needed —
            // no surprise navigation into a second editor.
            const savedToDashboard = !!(publicSavePayload && publicSavePayload.saved_to_dashboard);
            if (savedToDashboard && publicSavePayload.dashboard_url) {
                ensureDashboardLink(publicSavePayload.dashboard_url);
            }

            await clearDraft();

            state.basePdfBytes = new Uint8Array(editedBytes);
            state.annotations = [];
            state.selectedAnnotationId = null;
            state.history = [];
            state.redo = [];
            state.annotationDirty = false;
            state.formFieldOverrides.clear();
            finalizeWatermarkStateAfterSave({
                hasWatermarkChanges,
                hasVisualChanges,
            });

            state.protect.savedEnabled = state.protect.enabled;
            state.protect.savedHasPassword = state.protect.enabled ? state.protect.hasPassword : false;
            state.protect.pendingPassword = '';
            state.protect.showPassword = false;
            state.protect.showConfirm = false;
            syncProtectInputVisibility();
            syncProtectEnabledUi();
            updateProtectRulesUi();
            setProtectFeedback(getProtectDefaultFeedbackMessage());

            // Prefer server protection state when available.
            if (publicSavePayload && publicSavePayload.protection) {
                const savedEnabled = !!publicSavePayload.protection.enabled;
                const savedHasPassword = !!publicSavePayload.protection.has_password;
                state.protect.savedEnabled = savedEnabled;
                state.protect.savedHasPassword = savedHasPassword;
                state.protect.enabled = savedEnabled;
                state.protect.hasPassword = savedHasPassword;
                syncProtectEnabledUi();
                setProtectFeedback(getProtectDefaultFeedbackMessage());
            }

            syncDocumentNameUi();
            updateUndoRedoButtons();
            await renderAllPages();

            setStatus(savedToDashboard ? 'Saved to your dashboard' : 'Edits saved');
            if (!silentSuccess) {
                showAlert((publicSavePayload && publicSavePayload.message) || 'PDF edits saved successfully.');
            }
        } else {
            // The separate member editor is retired (Sep 2026): every save goes
            // through the session document, dashboard documents included.
            throw new Error('Save endpoint is unavailable for this document.');
        }
        return true;
    } catch (error) {
        showAlert(error.message || 'Save failed. Please try again.', true);
        setStatus('Save failed');
        return false;
    } finally {
        setButtonState(saveButton, false);
        if (saveCopyButton) {
            setButtonState(saveCopyButton, false);
        }
    }
}

export async function downloadEditedPdf() {
    commitAllInlineEditors();
    nudgeBlanksBeforeExport();

    setButtonState(downloadButton, true);
    setStatus('Preparing download...');

    try {
        if (hasPendingEdits()) {
            const saved = await saveEdits({
                silentNoEdits: true,
                silentSuccess: true,
            });

            if (!saved) {
                setStatus('Download canceled');
                return;
            }
        }

        if (publicEditorMode) {
            if (publicEditorDownloadUrl) {
                const response = await fetch(withCacheBust(publicDocUrl(publicEditorDownloadUrl)), {
                    credentials: 'same-origin',
                    cache: 'no-store',
                });

                if (!response.ok) {
                    const errorText = (await response.text()).trim();
                    let errorMessage = errorText;
                    try {
                        errorMessage = getApiErrorMessage(JSON.parse(errorText), errorText);
                    } catch (parseError) {
                        // Not JSON; keep the raw text.
                    }
                    throw new Error(errorMessage || 'Could not prepare PDF download.');
                }

                const pdfBlob = await response.blob();
                const fallbackName = `${sanitizeDownloadFileName(getBaseFileName())}.pdf`;
                const filename = parseDownloadFileName(response.headers.get('Content-Disposition'), fallbackName);
                downloadBlobObject(pdfBlob, filename);
            } else {
                const workingBytes = await getWorkingPdfBytesForConvert();
                downloadBlob(workingBytes, `${sanitizeDownloadFileName(getBaseFileName())}.pdf`);
            }
            setStatus('Download ready');
            maybePromptToolRatingAfterDownload();
            return;
        }

        if (!downloadUrl) {
            throw new Error('Download endpoint is unavailable for this document.');
        }

        const response = await fetch(downloadUrl, { credentials: 'same-origin' });
        if (!response.ok) {
            let message = 'Could not fetch PDF for download.';
            const errorText = (await response.text()).trim();
            if (errorText) {
                message = errorText;
            }
            throw new Error(message);
        }

        const pdfBlob = await response.blob();
        const fallbackName = `${sanitizeDownloadFileName(getBaseFileName())}.pdf`;
        const filename = parseDownloadFileName(response.headers.get('Content-Disposition'), fallbackName);
        downloadBlobObject(pdfBlob, filename);
        setStatus('Download ready');
    } catch (error) {
        showAlert(error.message || 'Download failed. Please try again.', true);
        setStatus('Download failed');
    } finally {
        setButtonState(downloadButton, false);
    }
}

export async function printEditedPdf() {
    setButtonState(printButton, true);
    setStatus('Preparing print...');

    try {
        if (hasPendingEdits()) {
            const saved = await saveEdits({
                silentNoEdits: true,
                silentSuccess: true,
            });

            if (!saved) {
                setStatus('Print canceled');
                return;
            }
        }

        if (!publicEditorMode && printUrl) {
            window.open(printUrl, '_blank', 'noopener');
            setStatus('Print preview opened');
            return;
        }

        const workingBytes = await getWorkingPdfBytesForConvert();
        const blob = new Blob([workingBytes], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(blobUrl, '_blank', 'noopener');
        if (!printWindow) {
            throw new Error('Pop-up blocked. Please allow pop-ups to print your PDF.');
        }

        window.setTimeout(() => {
            try {
                printWindow.focus();
                printWindow.print();
            } catch (error) {
                // Some browsers block immediate print calls. The opened tab still allows manual print.
            }
        }, 420);
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 45000);
        setStatus('Print preview opened');
    } catch (error) {
        showAlert(error.message || 'Print failed. Please try again.', true);
        setStatus('Print failed');
    } finally {
        setButtonState(printButton, false);
    }
}

export async function ensureSavedForSignatureSetup() {
    if (!hasPendingEdits()) {
        return true;
    }

    showAlert('Saving recent edits before opening signature setup...');
    return saveEdits({
        silentNoEdits: true,
        silentSuccess: true,
    });
}

export async function openSignatureSetup(mode = 'same', href = '') {
    if (publicEditorMode) {
        handlePublicSignatureAccess();
        return;
    }

    const destination = href || signatureRequestUrl;
    if (!destination) {
        showAlert('Signature setup route is unavailable for this document.', true);
        return;
    }

    const saved = await ensureSavedForSignatureSetup();
    if (!saved) {
        return;
    }

    if (mode === 'new') {
        window.open(destination, '_blank', 'noopener');
        return;
    }

    window.location.href = destination;
}
